import { Flex, Text, VStack } from '@chakra-ui/react';
import type { Deck } from '../../types/deck';
import {
  DeckSearchSuggestionRow,
  type DeckSearchSuggestionRowProps,
} from './DeckSearchSuggestionRow';

export interface DeckSearchSuggestionListProps {
  decks: Deck[];
  /** Optional section heading (e.g. "DECKS IN THIS VIEW"). */
  sectionLabel?: string;
  /** Shown when `decks` is empty. */
  emptyMessage?: string;
  maxHeight?: string;
  /**
   * Stretch to fill remaining parent height instead of a capped `maxHeight`.
   * Parent must be a flex column with a bounded height (`minH={0}`).
   */
  fillContainer?: boolean;
  /** Hide scrollbar while keeping overflow scroll (e.g. compact sidebar module). */
  hideScrollbar?: boolean;
  /** Prefix for row `id` attributes (autocomplete listbox). */
  rowIdPrefix?: string;
  getRowProps?: (deck: Deck, index: number) => Partial<DeckSearchSuggestionRowProps>;
}

export function DeckSearchSuggestionList({
  decks,
  sectionLabel,
  emptyMessage,
  maxHeight = 'min(420px, 62vh)',
  fillContainer = false,
  hideScrollbar = false,
  rowIdPrefix,
  getRowProps,
}: DeckSearchSuggestionListProps) {
  return (
    <>
      {sectionLabel ? (
        <Text fontSize="2xs" fontWeight="semibold" color="textMuted" px={1} pb={2} letterSpacing="wide">
          {sectionLabel}
        </Text>
      ) : null}

      {decks.length === 0 ? (
        emptyMessage ? (
          <Text fontSize="xs" color="textMuted" px={1} py={2}>
            {emptyMessage}
          </Text>
        ) : null
      ) : (
        <VStack
          align="stretch"
          gap={2}
          flex={fillContainer ? '1' : undefined}
          minH={fillContainer ? 0 : undefined}
          maxH={fillContainer ? undefined : maxHeight}
          overflowY="auto"
          css={
            hideScrollbar
              ? {
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }
              : { scrollbarWidth: 'thin' }
          }
        >
          {decks.map((deck, index) => (
            <DeckSearchSuggestionRow
              key={deck.id}
              deck={deck}
              id={rowIdPrefix ? `${rowIdPrefix}${deck.id}` : undefined}
              {...getRowProps?.(deck, index)}
            />
          ))}
        </VStack>
      )}
    </>
  );
}

/** Deck rows inside an autocomplete dropdown (gap + scroll container). */
export function DeckSearchSuggestionDropdownList({
  decks,
  sectionLabel,
  rowIdPrefix,
  getRowProps,
}: Pick<DeckSearchSuggestionListProps, 'decks' | 'sectionLabel' | 'rowIdPrefix' | 'getRowProps'>) {
  if (decks.length === 0) return null;

  return (
    <>
      {sectionLabel ? (
        <Text fontSize="2xs" fontWeight="semibold" color="textMuted" px={1} pb={2} letterSpacing="wide">
          {sectionLabel}
        </Text>
      ) : null}
      <Flex direction="column" gap={2}>
        {decks.map((deck, index) => (
          <DeckSearchSuggestionRow
            key={deck.id}
            deck={deck}
            id={rowIdPrefix ? `${rowIdPrefix}${deck.id}` : undefined}
            {...getRowProps?.(deck, index)}
          />
        ))}
      </Flex>
    </>
  );
}
